import * as Yup from "yup";
import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import { GRANT_CREDITS } from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";
import { requiredNumberRangeSchema } from "../../utils/PurchaseValidation";
import {
  CREDIT_BALANCE_MAX,
  CREDIT_TYPE_LABEL_KEY,
  GRANT_NOTE_MAX_LENGTH,
} from "../../utils/PurchaseConstants";
import { formatCredits } from "../../utils/PurchaseFormat";
import { ICreditBalanceCell } from "./CreditGrantViewController";

export interface ICreditGrantForm {
  credit_type: string;
  credits: number | null;
  note: string;
}

export const CREDIT_GRANT_INITIAL_VALUES: ICreditGrantForm = {
  credit_type: "",
  credits: null,
  note: "",
};

const requiredMessage = (t: TUseTranslationTfn, fieldName: string) =>
  prepareMessageFromParams(t("error_message_required"), [["fieldName", fieldName]]);

const findBalance = (balances: ICreditBalanceCell[], creditType: string) =>
  balances.find((cell) => cell.credit_type === creditType);

export const getGrantBlockMessage = (
  t: TUseTranslationTfn,
  balances: ICreditBalanceCell[],
  values: ICreditGrantForm,
): string | null => {
  const cell = findBalance(balances, values.credit_type);
  if (!cell) return null;
  const typeLabel = t(CREDIT_TYPE_LABEL_KEY[cell.credit_type]);
  if (cell.is_unlimited) {
    return prepareMessageFromParams(t("grant_unlimited"), [["type", typeLabel]]);
  }
  const credits = Number(values.credits);
  if (!Number.isInteger(credits) || credits <= 0) return null;
  const after = (cell.balance ?? 0) + credits;
  if (after <= CREDIT_BALANCE_MAX) return null;
  return prepareMessageFromParams(t("grant_over_cap"), [
    ["type", typeLabel],
    ["after", formatCredits(t, after)],
    ["max", formatCredits(t, CREDIT_BALANCE_MAX)],
    ["can_add", formatCredits(t, cell.max_purchasable)],
  ]);
};

export const creditGrantValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    credit_type: Yup.string().required(requiredMessage(t, t("credit_type"))),
    credits: requiredNumberRangeSchema(t, {
      fieldName: t("credits"),
      min: 1,
      max: CREDIT_BALANCE_MAX,
    }).integer(
      prepareMessageFromParams(t("error_message_integer"), [["fieldName", t("credits")]]),
    ),
    note: Yup.string()
      .trim()
      .required(requiredMessage(t, t("reason")))
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("reason")],
          ["min", "2"],
        ]),
      )
      .max(
        GRANT_NOTE_MAX_LENGTH,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("reason")],
          ["max", String(GRANT_NOTE_MAX_LENGTH)],
        ]),
      ),
  });

export const handleSubmitCreditGrant = async (
  firmId: string,
  values: ICreditGrantForm,
  setLoading: TReactSetState<boolean>,
  handleClose: (status: boolean) => void,
) => {
  setLoading(true);
  try {
    const response = await GRANT_CREDITS({
      firm_id: firmId,
      credit_type: values.credit_type,
      credits: Number(values.credits),
      note: values.note.trim(),
    });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      handleClose(true);
      return;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "handleSubmitCreditGrant");
  } finally {
    setLoading(false);
  }
};

